import { buildChannelMaps, type MappedChannel } from '$lib/utils/channel_lookup';
import type { UnitDomainGraphResponse } from '$lib/types/domain_graph';

type ProjectSummary = { name: string };

type KeypadAction = {
	id?: number;
	key?: number;
	event_type?: string;
	scene?: number | null;
	action_type?: string;
	action_target?: number;
	state?: string;
	value?: number;
	[key: string]: unknown;
};

type KeypadKey = {
	id: number;
	key_index: number;
	name?: string;
	group_id?: number;
	address?: number;
	actions?: KeypadAction[];
};

type KeypadDevice = {
	id: number;
	type: 'standard_keypad' | 'corridor_keypad' | 'logic_board' | 'keycard' | string;
	name?: string;
	location_room?: number;
	cat6_branch?: number;
	keys: KeypadKey[];
};

type KeyAction = KeypadAction & { key?: number };
type Scene = UnitDomainGraphResponse['layout']['scenes'][number];
type SceneLoad = NonNullable<Scene['scene_loads']>[number] & { scene: Scene['id'] };

export type ElectricalLayoutExportData = {
	project: ProjectSummary;
	unit: UnitDomainGraphResponse['unit'];
	zones: UnitDomainGraphResponse['layout']['zones'];
	rooms: UnitDomainGraphResponse['layout']['zones'][number]['rooms'][number][];
	loads: UnitDomainGraphResponse['layout']['zones'][number]['rooms'][number]['loads'][number][];
	rcu: NonNullable<UnitDomainGraphResponse['hardware']['rcu']> | null;
	rcus: NonNullable<UnitDomainGraphResponse['hardware']['rcu']>[];
	dinModules: NonNullable<UnitDomainGraphResponse['hardware']['rcu']>['din_modules'];
	channelsByLoadId: Map<number, MappedChannel>;
	allMappedChannels: MappedChannel[];
	keypads: KeypadDevice[];
	keypadKeys: (KeypadKey & { keypad: KeypadDevice['id'] })[];
	keyActions: KeyAction[];
	scenes: Scene[];
	sceneLoads: SceneLoad[];
};

type LegacyRoom = {
	id: number;
	name: string;
	loads?: Array<{ id: number; name?: string; load_type?: string; total_wattage?: string | number }>;
	keypads?: Array<{
		id: number;
		address: number;
		num_keys: number;
		cat6_branch: number;
		sub_type: string;
		inputs: Array<LegacyKeypadInput>;
	}>;
	logic_boards?: Array<{
		id: number;
		address: number;
		num_keys: number;
		cat6_branch: number;
		inputs: Array<LegacyKeypadInput>;
	}>;
	keycards?: Array<{
		id: number;
		address: number;
		num_keys: number;
		inputs: Array<LegacyKeypadInput>;
	}>;
	input_devices?: KeypadDevice[];
};

type LegacyKeypadInput = {
	id: number;
	key_index: number;
	name?: string;
	actions?: Array<KeypadAction>;
};

type LegacyRCU = {
	id: number;
	name?: string;
	channel_count?: number;
	channels?: Array<{ id: number; channel_number: number; load_id: number | null }>;
	din_modules?: Array<{
		id: number;
		address: number;
		module_type: string;
		channels?: Array<{ id: number; channel_number: number; load_id: number | null }>;
	}>;
};

function asLegacyRoom(room: unknown): LegacyRoom {
	return (room ?? {}) as LegacyRoom;
}

function mapKeypadType(subType: string): KeypadDevice['type'] {
	return subType === 'corridor' ? 'corridor_keypad' : 'standard_keypad';
}

function buildKeypadDevices(room: LegacyRoom): KeypadDevice[] {
	const devices: KeypadDevice[] = [];

	for (const kp of room.keypads ?? []) {
		devices.push({
			id: kp.id,
			type: mapKeypadType(kp.sub_type),
			name: `Keypad ${kp.address}`,
			location_room: room.id,
			cat6_branch: kp.cat6_branch,
			keys: (kp.inputs ?? []).map((input) => ({
				id: input.id,
				key_index: input.key_index,
				name: input.name ?? '',
				group_id: 0,
				address: 0,
				actions: (input.actions ?? []).map((action) => ({
					id: action.id,
					key: input.id,
					event_type: action.event_type,
					scene: action.action_target ?? null
				}))
			}))
		});
	}

	for (const lb of room.logic_boards ?? []) {
		devices.push({
			id: lb.id,
			type: 'logic_board',
			name: `Logic Board ${lb.address}`,
			location_room: room.id,
			cat6_branch: lb.cat6_branch,
			keys: (lb.inputs ?? []).map((input) => ({
				id: input.id,
				key_index: input.key_index,
				name: input.name ?? '',
				group_id: 3,
				address: lb.address ?? 0,
				actions: (input.actions ?? []).map((action) => ({
					id: action.id,
					key: input.id,
					event_type: action.event_type,
					scene: action.action_target ?? null
				}))
			}))
		});
	}

	for (const kc of room.keycards ?? []) {
		devices.push({
			id: kc.id,
			type: 'keycard',
			name: `Keycard ${kc.address}`,
			location_room: room.id,
			cat6_branch: 1,
			keys: (kc.inputs ?? []).map((input) => ({
				id: input.id,
				key_index: input.key_index,
				name: input.name ?? '',
				group_id: 0,
				address: 0,
				actions: (input.actions ?? []).map((action) => ({
					id: action.id,
					key: input.id,
					event_type: action.event_type,
					scene: action.action_target ?? null
				}))
			}))
		});
	}

	return devices;
}

function extractRCUs(domainGraph: UnitDomainGraphResponse): LegacyRCU[] {
	const hardware = (domainGraph as unknown as { hardware?: { rcus?: LegacyRCU[]; rcu?: LegacyRCU | LegacyRCU[] } }).hardware;
	if (!hardware) return [];
	const raw = (hardware as { rcus?: LegacyRCU[]; rcu?: LegacyRCU | LegacyRCU[] }).rcus;
	if (Array.isArray(raw)) return raw;
	const single = (hardware as { rcu?: LegacyRCU | LegacyRCU[] }).rcu;
	if (Array.isArray(single)) return single;
	if (single) return [single];
	return [];
}

export function buildExportData(domainGraph: UnitDomainGraphResponse): ElectricalLayoutExportData {
	const legacyRcus = extractRCUs(domainGraph);
	const firstRcu = legacyRcus[0] ?? null;
	const rcus = firstRcu ? [firstRcu as NonNullable<UnitDomainGraphResponse['hardware']['rcu']>] : [];
	const dinModules = (firstRcu?.din_modules ?? []) as NonNullable<UnitDomainGraphResponse['hardware']['rcu']>['din_modules'];

	const zones = domainGraph.layout.zones;
	const rooms = zones.flatMap((z) => z.rooms) as unknown as Array<LegacyRoom & { id: number; name: string }>;

	const inputDevices = rooms.flatMap((r) => {
		const legacy = asLegacyRoom(r);
		return legacy.input_devices ?? buildKeypadDevices(legacy);
	});
	const keypads = inputDevices.filter(
		(d) => d.type === 'standard_keypad' || d.type === 'corridor_keypad'
	);
	const loads = rooms.flatMap((r) => r.loads ?? []);
	const scenes = domainGraph.layout.scenes;
	const { channelsByLoadId, allMappedChannels } = buildChannelMaps(domainGraph);

	const keypadInputs: (KeypadKey & { keypad: number })[] = keypads.flatMap((k) =>
		k.keys.map((input) => ({ ...input, keypad: k.id }))
	);
	const keyActions: KeyAction[] = keypadInputs.flatMap((input) =>
		(input.actions || []).map((action) => ({ ...action, key: input.id }))
	);
	const sceneLoads = scenes.flatMap(
		(s) => (s.scene_loads || []).map((sl) => ({ ...sl, scene: s.id })) as SceneLoad[]
	);

	const firstSceneProject = scenes[0]?.project;
	const unitProject = (domainGraph.unit as { project?: unknown }).project;
	const projectName =
		(typeof unitProject === 'string' && unitProject.trim()) || firstSceneProject?.name || 'Project';
	const project: ProjectSummary = { name: projectName };

	return {
		project,
		unit: domainGraph.unit,
		zones,
		rooms: rooms as unknown as UnitDomainGraphResponse['layout']['zones'][number]['rooms'][number][],
		loads: loads as unknown as UnitDomainGraphResponse['layout']['zones'][number]['rooms'][number]['loads'][number][],
		rcu: firstRcu as NonNullable<UnitDomainGraphResponse['hardware']['rcu']>,
		rcus,
		dinModules,
		channelsByLoadId,
		allMappedChannels,
		keypads,
		keypadKeys: keypadInputs,
		keyActions,
		scenes,
		sceneLoads
	};
}
